import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Star, Calendar } from 'lucide-react';
import type { NewsItem } from '@/constants/news';
import { maybeTransliterate } from '@/utils/transliterate';
import { stripMarkdown } from '@/utils/stripMarkdown';

interface FeaturedArticleBannerProps {
  article: NewsItem;
  /** Routing prefix — 'article' (default), 'news' / 'program' (legacy / mirror) */
  type?: 'article' | 'news' | 'program';
}

export default function FeaturedArticleBanner({
  article,
  type = 'article',
}: FeaturedArticleBannerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'bs';

  const linkPath = `/${type}/${article.slug || article.id}`;

  const dateLocale =
    locale === 'sr-Cyrl' ? 'sr-Cyrl-RS' : locale === 'sr' ? 'sr-Latn-RS' : locale;

  const formattedDate = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const title = maybeTransliterate(article.title, locale);
  // Excerpt iz Strapi-ja može sadržavati Markdown sintaksu (**bold**, [link](...), itd.).
  // Banner ga renderuje u <p> sa line-clamp-3, pa skidamo Markdown da se ne prikazuje sirov.
  const rawExcerpt = article.excerpt || article.description || '';
  const cleanExcerpt = stripMarkdown(rawExcerpt);
  const excerpt = maybeTransliterate(cleanExcerpt, locale);

  return (
    <Link
      to={linkPath}
      className="group block relative overflow-hidden rounded-3xl border-2 border-accent-pink/30 hover:border-accent-pink/60 transition-all duration-500 mb-8 sm:mb-12"
      style={{
        background:
          'linear-gradient(135deg, rgba(236,72,153,0.08) 0%, rgba(168,85,247,0.08) 100%)',
      }}
      aria-label={title}
    >
      <div className="grid md:grid-cols-2 gap-0 items-stretch">
        <div className="relative h-64 sm:h-80 md:h-auto overflow-hidden">
          <img
            src={article.image}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-accent-pink to-purple-600 text-white text-xs font-bold uppercase tracking-wide shadow-lg">
            <Star size={12} fill="white" />
            <span>{t('news.featured')}</span>
          </div>
        </div>

        <div className="flex flex-col justify-center p-6 sm:p-8 md:p-10">
          {formattedDate && (
            <div className="flex items-center gap-2 text-text-muted text-sm mb-3">
              <Calendar size={14} />
              <span>{formattedDate}</span>
            </div>
          )}

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight group-hover:text-accent-pink transition-colors">
            {title}
          </h2>

          <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6 line-clamp-3">
            {excerpt}
          </p>

          <div className="inline-flex items-center gap-2 text-accent-pink font-semibold text-base group-hover:gap-3 transition-all">
            <span>{t('news.readFullArticle')}</span>
            <ArrowRight size={20} />
          </div>
        </div>
      </div>
    </Link>
  );
}
